/**
 * ShareCalculationSection Component
 *
 * Displays shareable URL for calculator results with copy-to-clipboard functionality.
 * Link is automatically generated when user completes calculation.
 *
 * Features:
 * - Automatic link generation (no button required)
 * - Copy to clipboard with visual feedback
 * - Error handling with user-friendly messages
 *
 * @module frontend/src/components/TariffCalculator/ShareCalculationSection
 */

import { useState } from 'react';
import {
  Paper,
  Title,
  Text,
  TextInput,
  ActionIcon,
  Alert,
  Stack,
} from '@mantine/core';
import {
  IconShare,
  IconCopy,
  IconCheck,
} from '@tabler/icons-react';

interface ShareCalculationSectionProps {
  /** Shareable URL (automatically generated) */
  shareUrl: string | null;
  /** Calculation year (e.g., 2025) */
  year: number;
}

/**
 * Share Calculation Section Component
 *
 * @param shareUrl - Automatically generated shareable URL
 * @param year - Calculation year
 *
 * @example
 * ```tsx
 * <ShareCalculationSection
 *   shareUrl={shareUrl}
 *   year={2025}
 * />
 * ```
 */
export function ShareCalculationSection({ shareUrl, year: _year }: ShareCalculationSectionProps) {
  const [copied, setCopied] = useState(false);

  /**
   * Handle "Copy to Clipboard" button click
   */
  const handleCopy = async () => {
    if (!shareUrl) {return;}

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      // Ignore clipboard errors
    }
  };

  // Don't render if no share URL available
  if (!shareUrl) {
    return null;
  }

  return (
    <Paper p="md" withBorder radius="md" shadow="sm">
      <Stack gap="md">
        {/* Header */}
        <div>
          <Title order={3} size="h4" mb={4}>
            <IconShare size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Link do udostępnienia
          </Title>
          <Text size="sm" c="dimmed">
            Link został wygenerowany automatycznie. Możesz go skopiować i udostępnić na forach lub w social media
          </Text>
        </div>

        {/* Share URL Display */}
        <Stack gap="xs">
          {/* URL Input with Copy Button */}
          <TextInput
            value={shareUrl}
            readOnly
            size="md"
            styles={{
              input: {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
            rightSection={
              <ActionIcon
                onClick={handleCopy}
                variant="subtle"
                color={copied ? 'green' : 'gray'}
                title={copied ? 'Skopiowano!' : 'Kopiuj do schowka'}
              >
                {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
              </ActionIcon>
            }
          />

          {/* Helper Text */}
          <Text size="xs" c="dimmed">
            Link zawiera wszystkie parametry kalkulacji. Możesz go udostępnić publicznie - nie zawiera żadnych danych osobowych. Link jest aktywny przez 2 lata.
          </Text>
        </Stack>

        {/* Info Alert */}
        <Alert color="blue" variant="light">
          <Text size="sm">
            Adres URL w oknie przeglądarki został zaktualizowany. Możesz skopiować go bezpośrednio z paska adresu lub użyć przycisku kopiowania powyżej.
          </Text>
        </Alert>
      </Stack>
    </Paper>
  );
}
